import type { Move, PersonalityId, Stance } from '@shared/protocol/messages';
import type { Rng } from './rng';
import { MOVES, counterOf } from './rps';
import { ultimateReady } from './engine';

/**
 * Monster AI. The signature makes cheating structurally impossible: the AI only
 * sees the player's moves from *previous* rounds, and it commits its choice
 * before the player is allowed to pick (see BattleController).
 */
export function chooseAiMove(personality: PersonalityId, playerHistory: readonly Move[], rng: Rng, level = 0): Move {
  const last = playerHistory[playerHistory.length - 1];
  const roll = rng.int(100);
  // Sharper monsters sometimes notice a habit: they counter the player's most used recent move.
  // Level 0 draws nothing extra, so classic monsters play exactly as before.
  if (level > 0) {
    const habit = favouriteOf(playerHistory.slice(-4));
    if (habit && rng.int(100) < (ADAPT_PERCENT[level] ?? 0)) return counterOf(habit);
  }
  switch (personality) {
    case 'stubborn':
      return weighted(roll, 'rock');
    case 'shy':
      return weighted(roll, 'paper');
    case 'sharp':
      return weighted(roll, 'scissors');
    case 'copycat':
      if (last && roll < 60) return last;
      return rng.pick(MOVES);
    case 'tricky':
      if (last && roll < 55) return counterOf(last);
      return rng.pick(MOVES);
    case 'chaotic':
    default:
      return rng.pick(MOVES);
  }
}

/** What the monster may look at when picking a stance: only state from *previous* rounds. */
export interface AiStanceContext {
  /** Monster's own stance cooldown (0 = ready). */
  cooldown: number;
  hp: number;
  maxHp: number;
  /** Monster's current win streak. */
  combo: number;
  foeHp: number;
  foeMaxHp: number;
  /** Monster's ink gauge (its ultimate is ready when full). */
  ink?: number;
}

/** Chance (%) that a monster fires its ultimate in a round where it is ready. */
export const AI_ULTIMATE_PERCENT = 70;

/** Percent chances [guard when low, focus when pressing, idle use] per personality. */
const STANCE_STYLE: Record<PersonalityId, { guard: number; focus: number; idle: number }> = {
  chaotic: { guard: 30, focus: 30, idle: 20 },
  stubborn: { guard: 15, focus: 50, idle: 10 },
  shy: { guard: 55, focus: 15, idle: 10 },
  sharp: { guard: 25, focus: 45, idle: 10 },
  copycat: { guard: 30, focus: 30, idle: 8 },
  tricky: { guard: 35, focus: 40, idle: 12 },
};

/**
 * Monster stance for the coming round, committed together with its move and
 * drawn from the monster's own Rng (never the combat Rng). Like the move, it
 * never sees the player's current choice: guard when hurt, focus with a combo
 * going or when the player is low, and now and then just for fun.
 * A full ink gauge fires the ultimate most of the time (it ignores the cooldown).
 * Always draws exactly one number when ready, none while cooling down without
 * a full gauge.
 */
export function chooseAiStance(personality: PersonalityId, ctx: AiStanceContext, rng: Rng): Stance {
  const ultReady = ultimateReady(ctx.ink ?? 0);
  if (ctx.cooldown > 0 && !ultReady) return 'none';
  const roll = rng.int(100);
  if (ultReady && roll < AI_ULTIMATE_PERCENT) return 'ultimate';
  if (ctx.cooldown > 0) return 'none';
  const style = STANCE_STYLE[personality] ?? STANCE_STYLE.chaotic;
  const low = ctx.hp * 100 < ctx.maxHp * 35;
  const pressing = ctx.combo >= 1 || ctx.foeHp * 100 < ctx.foeMaxHp * 30;
  if (low && roll < style.guard) return 'guard';
  if (pressing && roll < style.focus) return 'focus';
  if (roll >= 100 - style.idle) return roll % 2 === 0 ? 'guard' : 'focus';
  return 'none';
}

/** 60% favourite, 20% each of the others. */
function weighted(roll: number, favourite: Move): Move {
  if (roll < 60) return favourite;
  const others = MOVES.filter((m) => m !== favourite);
  return (roll < 80 ? others[0] : others[1]) ?? favourite;
}

/**
 * Chance (%) per AI level that the monster counters the player's recent habit
 * instead of following its own. Against varied play this changes nothing (the
 * player's next move is not the habit); against spamming one move it bites.
 */
const ADAPT_PERCENT = [0, 25, 40, 50];

/**
 * How sharp a monster of this level plays: 0 = classic habits (the first three
 * stages, while players learn), then sharper and sharper: 1 = levels 4-10,
 * 2 = levels 11-24, 3 = level 25 and up.
 */
export function aiLevelFor(monsterLevel: number): number {
  return monsterLevel >= 25 ? 3 : monsterLevel >= 11 ? 2 : monsterLevel >= 4 ? 1 : 0;
}

/** The most played move (at least twice), or null. A tie goes to the one played last. */
function favouriteOf(moves: readonly Move[]): Move | null {
  let best: Move | null = null;
  let bestCount = 1;
  for (let i = moves.length - 1; i >= 0; i--) {
    const m = moves[i]!;
    const n = moves.filter((x) => x === m).length;
    if (n > bestCount) {
      best = m;
      bestCount = n;
    }
  }
  return best;
}
